/**
 * Matchning av en kund (org.nr, namn, e-postdomän) mot CRM-kunderna.
 *
 * Ren logik utan importer, så att den kan användas både i appen och av
 * Node-skript (scripts/flatbox-link-leads.mjs). Exporteras vidare från
 * bokningsmail.js.
 */

/** E-postdomäner som inte säger något om vilket företag kontakten jobbar på. */
export const GENERISKA_DOMANER = new Set([
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.se', 'outlook.com', 'outlook.se',
  'live.com', 'live.se', 'msn.com', 'icloud.com', 'me.com', 'mac.com',
  'yahoo.com', 'yahoo.se', 'telia.com', 'telia.se', 'tele2.se', 'comhem.se', 'bredband.net',
  'bahnhof.se', 'spray.se', 'passagen.se', 'aol.com', 'protonmail.com', 'proton.me', 'gmx.com',
]);

/** Tio siffror ur ett inklistrat värde (sekelprefix 16 tas bort), annars ''. */
export function orgNrSiffror(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('16')) d = d.slice(2);
  return /^\d{10}$/.test(d) ? d : '';
}

/** "Exempelbygg Aktiebolag" → "exempelbygg" */
export function normaliseraForetagsnamn(namn) {
  return String(namn || '')
    .toLowerCase()
    .replace(/\(publ\)/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\b(aktiebolag|ab|handelsbolag|hb|kommanditbolag|kb)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function domanFranEpost(epost) {
  const m = /@([^@\s>]+)$/.exec(String(epost || '').trim().toLowerCase());
  return m ? m[1] : '';
}

export function arForetagsdoman(doman) {
  return !!doman && !GENERISKA_DOMANER.has(doman);
}

/** "https://www.exempelbygg.se/om" → "exempelbygg.se" */
export function domanFranWebbplats(url) {
  const s = String(url || '').trim().toLowerCase();
  if (!s) return '';
  const host = s.replace(/^[a-z]+:\/\//, '').split(/[/?#]/)[0];
  return host.replace(/^www\./, '');
}

/**
 * Kund: org.nr, därefter normaliserat namn, därefter företagsdomän mot
 * kundens webbplats. Första steget som ger träff vinner.
 * → { via: 'orgnr'|'namn'|'doman'|null, kandidater: [company] }
 */
export function matchaKund({ orgNr, namn, doman }, kunder) {
  const nr = orgNrSiffror(orgNr);
  if (nr) {
    const k = kunder.filter(c => orgNrSiffror(c.orgNumber) === nr);
    if (k.length) return { via: 'orgnr', kandidater: k };
  }
  const n = normaliseraForetagsnamn(namn);
  if (n) {
    const k = kunder.filter(c => normaliseraForetagsnamn(c.name) === n);
    if (k.length) return { via: 'namn', kandidater: k };
  }
  if (doman) {
    const k = kunder.filter(c => domanFranWebbplats(c.website) === doman);
    if (k.length) return { via: 'doman', kandidater: k };
  }
  return { via: null, kandidater: [] };
}

/** En Flatbox-offerts kund (offer{}) mot CRM-kunderna, som i analysexporten. */
export function matchaFlatboxOffertKund(off, kunder) {
  const doman = domanFranEpost(off?.custEmail);
  return matchaKund({
    orgNr: off?.custOrg,
    namn: off?.custName,
    doman: arForetagsdoman(doman) ? doman : '',
  }, kunder);
}
